import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

// Matches the Supabase app_role enum — keep in sync with migrations
export type AppRole =
  | "admin"
  | "manager"
  | "finance_manager"
  | "hr_manager"
  | "quality"
  | "production"
  | "npd"
  | "sales"
  | "viewer"
  | "supervisor"
  | "user"
  | "tech"
  | "moderator";

/** Human-readable labels for each role */
export const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  manager: "Manager",
  finance_manager: "Finance Manager",
  hr_manager: "HR Manager",
  quality: "Quality",
  production: "Production",
  npd: "NPD",
  sales: "Sales",
  viewer: "Viewer",
  supervisor: "Supervisor",
  user: "User",
  tech: "Tech",
  moderator: "Moderator",
};

/** Roles that should appear in the Admin UI role selector */
export const ASSIGNABLE_ROLES: AppRole[] = [
  "admin",
  "manager",
  "finance_manager",
  "hr_manager",
  "quality",
  "production",
  "npd",
  "sales",
  "supervisor",
  "viewer",
  "user",
];

/** Roles that have custom permission options in the permissions matrix */
export const PERMISSION_MATRIX_ROLES: AppRole[] = [
  "admin",
  "manager",
  "finance_manager",
  "hr_manager",
  "quality",
  "production",
  "npd",
  "sales",
  "viewer",
];

interface UserRole {
  role: AppRole;
}

export function useUserRole() {
  const { user } = useAuth();

  const { data: roles, isLoading, isError, refetch } = useQuery({
    queryKey: ["user-roles", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      // Why this THROWS rather than degrading to []:
      // `user_roles` is the bedrock of the entire access gate — every module
      // permission is derived from it (see useModuleAccess). Swallowing the error
      // and returning [] makes "we could not read your roles" byte-identical to
      // "you have no roles", which renders as a total, silent lockout for a full
      // admin. Worse, an UNAUTHENTICATED read of this table returns HTTP 200 with
      // an empty array (the RLS policy filters rather than raising), so the empty
      // case is already ambiguous without us adding a second way to reach it.
      // Throwing lets React Query expose isError, so the guard can say "could not
      // verify" instead of "denied". Contrast fetchUserModuleOverrides, which is an
      // OPTIONAL layer and deliberately fails OPEN.
      if (error) throw error;
      return (data || []) as UserRole[];
    },
    enabled: !!user,
  });

  const hasRole = (role: AppRole): boolean => {
    return roles?.some((r) => r.role === role) ?? false;
  };

  const isAdmin = hasRole("admin");
  const isTech = hasRole("tech");
  const isManager = hasRole("manager") || isAdmin;
  const isFinanceManager = hasRole("finance_manager") || isAdmin;
  // HR profile data is admin-or-HR only; admins inherit it via the module override.
  const isHrManager = hasRole("hr_manager") || isAdmin;
  const isSupervisor = hasRole("supervisor") || isManager;

  return {
    roles: roles?.map((r) => r.role) ?? [],
    /**
     * True when the roles read FAILED. Callers must treat this as "unknown",
     * never as "no roles" — see the throw above.
     */
    isError,
    refetchRoles: refetch,
    isAdmin,
    isTech,
    isManager,
    isFinanceManager,
    isHrManager,
    isSupervisor,
    hasRole,
    isLoading,
  };
}
